import { Injectable } from '@angular/core';
import { PlatformService } from '../platform-service/platform.service';

@Injectable({ providedIn: 'root' })
export class ScrollService {
  private static readonly RESTORE_RETRY_TIMEOUT_MS = 1000;

  private readonly scrollPositions = new Map<string, number>();
  private readonly horizontalElementScrollPositions = new Map<string, number>();
  private scrolling = false;

  private allowWithin: HTMLElement | null = null;
  private currentScrollable: HTMLElement | null = null;

  private touchStartHandler?: (e: TouchEvent) => void;
  private touchMoveHandler?: (e: TouchEvent) => void;
  private wheelHandler?: (e: WheelEvent) => void;

  private lastTouchY: number | null = null;

  constructor(private readonly platformService: PlatformService) {}

  savePosition(key: string): void {
    this.scrollPositions.set(key, window.scrollY);
  }

  restorePosition(key: string, delay: number = 0): void {
    const position = this.scrollPositions.get(key) ?? 0;
    this.enableScroll();
    this.forgetPosition(key);
    this.scrollOnceReachable(position, delay);
  }

  restorePositionIfAvailable(key: string, delay: number = 0): void {
    const position = this.scrollPositions.get(key);
    if (position === undefined) return;

    this.enableScroll();
    this.forgetPosition(key);
    this.scrollOnceReachable(position, delay);
  }

  /**
   * A list is still rendering when its data arrives, so the document is too short for the saved
   * offset and the browser clamps the jump back to the top. Retry every frame until the page has
   * grown enough to hold the position, and give up after RESTORE_RETRY_TIMEOUT_MS so an offset that
   * can no longer be reached - a deleted item left the list shorter - does not keep polling.
   */
  private scrollOnceReachable(position: number, delay: number): void {
    setTimeout(() => {
      const deadline = Date.now() + ScrollService.RESTORE_RETRY_TIMEOUT_MS;

      const step = () => {
        window.scrollTo(0, position);
        if (window.scrollY < position && Date.now() < deadline) {
          requestAnimationFrame(step);
        }
      };

      step();
    }, delay);
  }

  saveHorizontalPosition(key: string, position: number): void {
    this.horizontalElementScrollPositions.set(key, position);
  }

  restoreHorizontalElementPosition(key: string, element: HTMLElement, delay: number = 0): void {
    const position = this.horizontalElementScrollPositions.get(key);
    if (position === undefined) return;

    setTimeout(() => {
      element.scrollTo({ left: position });
    }, delay);
  }

  disableScroll(allowScrollWithin?: HTMLElement): void {
    if (this.platformService.isMobile()) {
      this.enableScroll();

      this.allowWithin = allowScrollWithin || null;

      const isInsideAllowed = (target: EventTarget | null) =>
        !!(this.allowWithin && target instanceof Node && this.allowWithin.contains(target));

      const findScrollableAncestor = (start: HTMLElement | null): HTMLElement | null => {
        let el: HTMLElement | null = start;
        while (el && this.allowWithin && this.allowWithin.contains(el)) {
          const style = getComputedStyle(el);
          const overflowY = style.overflowY;
          const canScrollY = el.scrollHeight > el.clientHeight;
          const scrollable = canScrollY && (overflowY === 'auto' || overflowY === 'scroll');
          if (scrollable) return el;
          el = el.parentElement;
        }
        return null;
      };

      const canScrollInDirection = (el: HTMLElement, deltaY: number) => {
        if (deltaY > 0) {
          return el.scrollTop + el.clientHeight < el.scrollHeight;
        } else if (deltaY < 0) {
          return el.scrollTop > 0;
        }
        return false;
      };

      (document.body.style as any).overscrollBehavior = 'none';
      (document.documentElement.style as any).overscrollBehavior = 'none';

      this.touchStartHandler = (e: TouchEvent) => {
        this.lastTouchY = e.touches?.[0]?.clientY ?? null;
        const targetEl = (e.target as HTMLElement) ?? null;
        this.currentScrollable = isInsideAllowed(e.target)
          ? findScrollableAncestor(targetEl) || this.allowWithin
          : null;
      };

      this.touchMoveHandler = (e: TouchEvent) => {
        if (e.touches.length > 1) {
          e.preventDefault();
          return;
        }

        const currentY = e.touches?.[0]?.clientY ?? this.lastTouchY;
        const prevY = this.lastTouchY ?? currentY;
        this.lastTouchY = currentY;
        const deltaY = prevY !== null && currentY !== null ? prevY - currentY : 0;

        if (!this.allowWithin) {
          e.preventDefault();
          return;
        }

        if (!isInsideAllowed(e.target)) {
          e.preventDefault();
          return;
        }

        const scrollable = this.currentScrollable || this.allowWithin;
        if (!scrollable || !canScrollInDirection(scrollable, deltaY)) {
          e.preventDefault();
          return;
        }
      };

      this.wheelHandler = (e: WheelEvent) => {
        if (!this.allowWithin) {
          e.preventDefault();
          return;
        }

        if (!isInsideAllowed(e.target)) {
          e.preventDefault();
          return;
        }

        const targetEl = (e.target as HTMLElement) ?? null;
        const scrollable = findScrollableAncestor(targetEl) || this.allowWithin;

        if (!scrollable) {
          e.preventDefault();
          return;
        }

        if (!canScrollInDirection(scrollable, e.deltaY)) {
          e.preventDefault();
        }
      };

      document.body.addEventListener('touchstart', this.touchStartHandler, {
        passive: true,
        capture: true,
      });
      document.body.addEventListener('touchmove', this.touchMoveHandler, {
        passive: false,
        capture: true,
      });
      document.body.addEventListener('wheel', this.wheelHandler, {
        passive: false,
        capture: true,
      });

      return;
    }

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
  }

  enableScroll(): void {
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    (document.body.style as any).overscrollBehavior = '';
    (document.documentElement.style as any).overscrollBehavior = '';

    if (this.touchStartHandler) {
      document.body.removeEventListener('touchstart', this.touchStartHandler, {
        capture: true,
      } as any);
      this.touchStartHandler = undefined;
    }
    if (this.touchMoveHandler) {
      document.body.removeEventListener('touchmove', this.touchMoveHandler, {
        capture: true,
      } as any);
      this.touchMoveHandler = undefined;
    }
    if (this.wheelHandler) {
      document.body.removeEventListener('wheel', this.wheelHandler, { capture: true } as any);
      this.wheelHandler = undefined;
    }

    this.allowWithin = null;
    this.currentScrollable = null;
    this.lastTouchY = null;
  }

  setIsScrolling() {
    this.scrolling = true;
  }
  setFinishScroll() {
    this.scrolling = false;
  }
  isFinishScroll() {
    return !this.scrolling;
  }

  forgetPosition(key: string): void {
    this.scrollPositions.delete(key);
  }

  resetScrollPosition(element?: HTMLElement): void {
    this.enableScroll();
    setTimeout(() => {
      if (element) element.scrollTo(0, 0);
      else window.scrollTo(0, 0);
    });
  }
}
